import {useCallback, useReducer} from 'react'
import {useTranslation} from 'react-i18next'
import {validateConfirmPassword, validateEmail, validatePassword} from '../utils/authValidation'
import React from 'react'

interface AuthFormValues {
  email: string;
  password: string;
  confirmPassword: string;
  rememberMe: boolean;
}

interface AuthFormTouched {
  email: boolean;
  password: boolean;
  confirmPassword: boolean;
}

interface AuthFormErrors {
  email: string;
  password: string;
  confirmPassword: string;
}

interface AuthFormDirty {
  email: boolean;
  password: boolean;
  confirmPassword: boolean;
}

interface AuthFormState {
  values: AuthFormValues;
  touched: AuthFormTouched;
  dirty: AuthFormDirty;
  errors: AuthFormErrors;
  isLoading: boolean;
}

type AuthFormField = keyof AuthFormValues;
type ValidatableField = keyof AuthFormErrors;

type AuthFormAction =
  | { type: 'SET_FIELD_VALUE'; field: AuthFormField; value: string | boolean }
  | { type: 'SET_FIELD_TOUCHED'; field: ValidatableField }
  | { type: 'SET_FIELD_DIRTY'; field: ValidatableField }
  | { type: 'SET_FIELD_ERROR'; field: ValidatableField; error: string }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'RESET_FORM' }
  | { type: 'RESET_ERRORS' };

const initialState: AuthFormState = {
  values: {
    email: '',
    password: '',
    confirmPassword: '',
    rememberMe: false
  },
  touched: {
    email: false,
    password: false,
    confirmPassword: false
  },
  dirty: {
    email: false,
    password: false,
    confirmPassword: false
  },
  errors: {
    email: '',
    password: '',
    confirmPassword: ''
  },
  isLoading: false
}

const authFormReducer = (state: AuthFormState, action: AuthFormAction): AuthFormState => {
  switch (action.type) {
    case 'SET_FIELD_VALUE':
      return {
        ...state,
        values: {
          ...state.values,
          [action.field]: action.value
        }
      }
    case 'SET_FIELD_TOUCHED':
      return {
        ...state,
        touched: {
          ...state.touched,
          [action.field]: true
        }
      }
    case 'SET_FIELD_DIRTY':
      return {
        ...state,
        dirty: {
          ...state.dirty,
          [action.field]: true
        }
      }
    case 'SET_FIELD_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.field]: action.error
        }
      }
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.isLoading
      }
    case 'RESET_FORM':
      return initialState
    case 'RESET_ERRORS':
      return {
        ...state,
        errors: {
          email: '',
          password: '',
          confirmPassword: ''
        },
        touched: {
          email: false,
          password: false,
          confirmPassword: false
        },
        dirty: {
          email: false,
          password: false,
          confirmPassword: false
        }
      }
    default:
      return state
  }
}

interface UseAuthFormReturn {
  values: AuthFormValues;
  touched: AuthFormTouched;
  errors: AuthFormErrors;
  isLoading: boolean;
  handleFieldChange: (field: AuthFormField) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFieldBlur: (field: ValidatableField) => () => void;
  setLoading: (isLoading: boolean) => void;
  isFormValid: () => boolean;
  resetForm: () => void;
  resetErrors: () => void;
}

export const useAuthForm = (isRegistering: boolean = false): UseAuthFormReturn => {
  const [state, dispatch] = useReducer(authFormReducer, initialState)
  const { t } = useTranslation()

  const setFieldValue = useCallback((field: AuthFormField, value: string | boolean) => {
    dispatch({ type: 'SET_FIELD_VALUE', field, value })
  }, [])

  const setFieldTouched = useCallback((field: ValidatableField) => {
    dispatch({ type: 'SET_FIELD_TOUCHED', field })
  }, [])

  const setFieldError = useCallback((field: ValidatableField, error: string) => {
    dispatch({ type: 'SET_FIELD_ERROR', field, error })
  }, [])

  const setFieldDirty = useCallback((field: ValidatableField) => {
    dispatch({ type: 'SET_FIELD_DIRTY', field })
  }, [])

  const setLoading = useCallback((isLoading: boolean) => {
    dispatch({ type: 'SET_LOADING', isLoading })
  }, [])

  const validateField = useCallback((field: ValidatableField, value: string): string => {
    let error = ''

    switch (field) {
      case 'email':
        error = validateEmail(value, t)
        break
      case 'password':
        error = validatePassword(value, t, isRegistering)
        break
      case 'confirmPassword':
        error = isRegistering ? validateConfirmPassword(value, state.values.password, t) : ''
        break
      default:
        break
    }

    return error
  }, [t, isRegistering, state.values.password])

  const handleFieldChange = useCallback((field: AuthFormField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'rememberMe' ? e.target.checked : e.target.value
    setFieldValue(field, value)

    // Mark field as dirty when user types (for validatable fields only)
    if (field !== 'rememberMe') {
      setFieldDirty(field)
    }

    if (field !== 'rememberMe' && state.touched[field]) {
      const error = validateField(field, value as string)
      setFieldError(field, error)
    }

    if (field === 'password' && isRegistering && state.touched.confirmPassword) {
      const confirmError = validateConfirmPassword(state.values.confirmPassword, value as string, t)
      setFieldError('confirmPassword', confirmError)
    }
  }, [state.touched, state.values.confirmPassword, validateField, setFieldValue, setFieldError, setFieldDirty, isRegistering, t])

  const handleFieldBlur = useCallback((field: ValidatableField) => () => {
    setFieldTouched(field)
    // Only validate on blur if user has typed something (dirty) or field has a value
    if (state.dirty[field] || state.values[field]) {
      const error = validateField(field, state.values[field])
      setFieldError(field, error)
    }
  }, [state.values, state.dirty, validateField, setFieldTouched, setFieldError])

  const isFormValid = useCallback((): boolean => {
    if (!isRegistering) {
      return !!(state.values.email && state.values.password && !validateEmail(state.values.email, t))
    } else {
      return !!(
        state.values.email &&
        state.values.password &&
        state.values.confirmPassword &&
        !validateEmail(state.values.email, t) &&
        !validatePassword(state.values.password, t, true) &&
        !validateConfirmPassword(state.values.confirmPassword, state.values.password, t)
      )
    }
  }, [isRegistering, state.values, t])

  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
  }, [])

  const resetErrors = useCallback(() => {
    dispatch({ type: 'RESET_ERRORS' })
  }, [])

  return {
    values: state.values,
    touched: state.touched,
    errors: state.errors,
    isLoading: state.isLoading,
    handleFieldChange,
    handleFieldBlur,
    setLoading,
    isFormValid,
    resetForm,
    resetErrors
  }
}
